/*
 * Models for Users
 */

define([
  "backbone",
  "messaging/messenger",
  "configuration/properties",
  "jquery",
  "underscore",
  "modules/topicsSplit/topic/topicModel",
  "modules/shared/sortableList/sortableListModel",
  "modules/accountAdmin/tenantAdmin/tenantModel",
], function (Backbone, Messenger, Properties, $, _, TopicModel, SortableListModel) {
  "use strict";

  const LETTER_REGEX = /[a-zA-ZÀ-žα-ωΑ-ώа-я]/i;
  const NUMBER_REGEX = /[0-9]/;
  const SYMBOL_VALUES = "~!@#$%^&*_-+=`|\\(){}[]:;\"'<>,.?/";
  const USER_STATUS = {
    DISABLED: 0,
    ENABLED: 1,
    LOCKED: 2,
  };
  const USER_STATUS_ALL = -1;
  const EMPTY_PASSWORD_PLACEHOLDER = "__password__";

  const UsersModel = {};

  UsersModel.USER_STATUS = USER_STATUS;
  UsersModel.USER_STATUS_ALL = USER_STATUS_ALL;
  UsersModel.EMPTY_PASSWORD_PLACEHOLDER = EMPTY_PASSWORD_PLACEHOLDER;

  UsersModel.validatePassword = function (password, userLogin, userFullName, tenant) {
    if (!password || !password.length) {
      return $.t("user.missingPassword");
    }
    if (!tenant) {
      tenant = Messenger.reqres.request("active:tenant");
    }
    const passwordLength = password.length;
    const passwordMinLength = tenant.get("passwordMinLength");
    if (passwordLength < passwordMinLength) {
      return $.t("user.shortPassword", tenant.attributes);
    }
    if (passwordLength > 100) {
      return $.t("commonMessages.tooLong", { number: 100, current: passwordLength });
    }
    const lowerPassword = password.toLowerCase();
    if (userFullName) {
      let lowerUserName = userFullName.toLowerCase();
      if (lowerPassword.indexOf(lowerUserName) >= 0) {
        return $.t("user.passwordCannotContainUserFullName");
      }
      // Remove all whitespace and check again
      lowerUserName = lowerUserName.replace(/\s+/g, "");
      if (lowerPassword.indexOf(lowerUserName) >= 0) {
        return $.t("user.passwordCannotContainUserFullName");
      }
    }
    if (userLogin) {
      const lowerLoginName = userLogin.toLowerCase();
      if (lowerPassword.indexOf(lowerLoginName) >= 0) {
        return $.t("user.passwordCannotContainUserLogin");
      }
    }
    let ch;
    const characterGroups = {
      lowerCase: 0,
      upperCase: 0,
      numbers: 0,
      symbols: 0,
      other: 0,
    };
    for (let i = 0; i < passwordLength; ++i) {
      ch = password[i];

      if (NUMBER_REGEX.exec(ch)) {
        ++characterGroups.numbers;
      } else if (LETTER_REGEX.exec(ch)) {
        // Trying to make separate regular expressions for uppercase and lowercase is very unwieldy in JavaScript.
        // Much easier to have a single letter regular expression and then use string.toLowerCase() to see if it is
        // an uppercase or lowercase letter.
        if (ch.toLowerCase() === ch) {
          ++characterGroups.lowerCase;
        } else {
          ++characterGroups.upperCase;
        }
      } else if (SYMBOL_VALUES.indexOf(ch) >= 0) {
        ++characterGroups.symbols;
      } else {
        ++characterGroups.other;
      }
    }
    let complexity = 0;
    if (characterGroups.lowerCase) {
      ++complexity;
    }
    if (characterGroups.upperCase) {
      ++complexity;
    }
    if (characterGroups.numbers) {
      ++complexity;
    }
    if (characterGroups.symbols) {
      ++complexity;
    }
    if (characterGroups.other) {
      ++complexity;
    }
    const passwordComplexity = tenant.get("passwordComplexity");
    if (complexity < passwordComplexity) {
      return $.t("user.weakPassword", tenant.attributes);
    }
    // Don't return anything (which implicitly returns undefined) if the password is valid
  };

  Messenger.reqres.setHandler("user:validatePassword", function (options) {
    let userLogin, password, userFullName, tenant;
    if (options) {
      userLogin = options.userLogin || options.loginName;
      password = options.password;
      userFullName = options.userFullName || options.userName;
      tenant = options.tenant;
    }
    return UsersModel.validatePassword(password, userLogin, userFullName, tenant);
  });

  UsersModel.userKbRole = Backbone.Model.extend({
    idAttribute: "id",
  });

  UsersModel.userKbRoles = Backbone.AtomicCollection.extend({
    model: UsersModel.userKbRole,
    url: function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      let userId;
      if (!this.userId) {
        userId = 0;
      } else {
        userId = this.userId;
      }
      return UsersModel.getTenantUrlFragment(tenantId) + "/" + userId + "/kbRoles";
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.reset(this.backupCol.models);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      const col = new UsersModel.userKbRoles();
      this.each(function (model) {
        col.add(model.clone());
      });
      this.backupCol = col;
    },
  });

  UsersModel.userRole = Backbone.Model.extend({
    idAttribute: "kbId",
    defaults: {
      authoring: false,
      reporting: false,
      reviewer: false,
      reviewerWithAttributes: false,
      adHocReporting: false,
      adHocReportingAdmin: false,
      topicImportAPI: false,
    },
  });

  UsersModel.userRoles = Backbone.AtomicCollection.extend({
    model: UsersModel.userRole,
    createUserRole: function (kbId) {
      this.add({
        authoring: false,
        reporting: false,
        adHocReporting: false,
        adHocReportingAdmin: false,
        topicImportAPI: false,
        kbId: kbId,
      });
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.reset(this.backupCol.models);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    validate: function () {
      let topicImportKbId = null;
      let errorMessage;
      this.forEach((role) => {
        if (role.get("topicImportAPI")) {
          if (topicImportKbId === null) {
            topicImportKbId = role.get("kbId");
          } else {
            errorMessage = $.t("user.singleTopicImportApiKb");
          }
        }
      });
      return errorMessage;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      const col = new UsersModel.userRoles();
      this.each(function (model) {
        col.add(model.clone());
      });
      this.backupCol = col;
    },
  });

  UsersModel.userTouchpoint = Backbone.Model.extend({
    idAttribute: "touchpointId",
    defaults: {
      touchpointId: "",
      name: "DEFAULT",
      hasPermissions: false,
      kbId: 1000,
    },
  });

  UsersModel.userTouchpoints = Backbone.AtomicCollection.extend({
    model: UsersModel.userTouchpoint,
    createUserTouchpoint: function (touchpointId, name, kbId) {
      this.add({ name: name, touchpointId: touchpointId, kbId: kbId });
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.reset(this.backupCol.models);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      const col = new UsersModel.userTouchpoints();
      this.each(function (model) {
        col.add(model.clone());
      });
      this.backupCol = col;
    },
  });

  UsersModel.knowledgeBase = Backbone.Model.extend({});

  Messenger.reqres.setHandler("user:touchpoints", function (userLoginId) {
    const userTouchpoints = new UsersModel.userTouchpoints();
    userTouchpoints.url = function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      if (typeof userLoginId == "number") {
        return UsersModel.getTenantUrlFragment(tenantId) + "/" + userLoginId + "/touchpoints";
      }
      return UsersModel.getTenantUrlFragment(tenantId) + "/login/" + userLoginId + "/touchpoints";
    };
    userTouchpoints.fetch();
    return userTouchpoints;
  });

  UsersModel.userContext = Backbone.Model.extend({
    idAttribute: "contextId",
    defaults: {
      contextId: "",
      contextName: "ACTIVE_ALL",
      hasPermissions: false,
      kbId: 1000,
    },
  });

  UsersModel.userContexts = Backbone.AtomicCollection.extend({
    model: UsersModel.userContext,
    createUserContext: function (contextId, name, kbId) {
      this.add({ contextName: name, contextId: contextId, kbId: kbId });
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.reset(this.backupCol.models);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      const col = new UsersModel.userContexts();
      this.each(function (model) {
        col.add(model.clone());
      });
      this.backupCol = col;
    },
  });

  Messenger.reqres.setHandler("user:contexts", function (userLoginId) {
    const userContexts = new UsersModel.userContexts();
    userContexts.url = function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      return UsersModel.getTenantUrlFragment(tenantId) + "/login/" + userLoginId + "/contexts";
    };
    userContexts.fetch();
    return userContexts;
  });

  UsersModel.tenantUser = Backbone.Model.extend({
    idAttribute: "userId",
    defaults: {
      userLogin: "",
      userFullName: "",
      userCreationDate: null,
      enabledStatus: USER_STATUS.ENABLED,
      isAdmin: false,
      userEmail: "",
      userId: null,
      userOrganization: "",
      userPhone: "",
      showPassword: false,
      password: "",
      reenterPassword: "",
      botCompany: "",
      botEmail: "",
      userDepartments: null,
      kbToKbRolesMap: null,
      lastLoginDate: null,
    },
    defaultValidateAttributes: [
      "userLogin",
      "userFullName",
      "password",
      "reenterPassword",
      "userEmail",
      "userOrganization",
      "userPhone",
      "roles",
    ],
    isTenantUser: function () {
      return true;
    },
    url: function () {
      let tenantId = this.get("tenantId");
      if (!tenantId && this.collection) {
        tenantId = this.collection.tenantId;
      }
      if (!tenantId) {
        tenantId = Messenger.reqres.request("active:tenantId");
      }
      let url = UsersModel.getTenantUrlFragment(tenantId);
      if (this.id) {
        url += "/" + this.id;
      }
      return url;
    },
    toJSON: function (options) {
      const json = this.constructor.__super__.toJSON.apply(this, arguments);
      if (options && options.saving) {
        delete json.reenterPassword;
        delete json.showPassword;
      }
      return json;
    },
    saveModel: function (options) {
      const modelOptions = this._prepareSaveModelOptions(options);
      if (this._allowSave(options)) {
        const savedShowPassword = this.get("showPassword");
        this.set("showPassword", false); //reset it to false
        const result = this.save(null, modelOptions);
        if (!result) {
          this.set("showPassword", savedShowPassword);
        }
        return result;
      }
      //fire success callback because nothing needed to be saved
      modelOptions.success(this, {}, options);
    },

    saveChildren: function (options) {
      //pass all the data needed to the child models for saving
      this.kbRoles().userId = this.get("userId");
      this.kbRoles().url = this.get("kbRoles").href;
      this.roles().userId = this.get("userId");
      this.roles().url = this.get("roles").href;
      this.touchpoints().userId = this.get("userId");
      this.touchpoints().url = this.get("touchpoints").href;
      this.contexts().userId = this.get("userId");
      this.contexts().url = this.get("contexts").href;
      //save all the child models and collections of the topic
      this.saveSubModels(options);
    },

    //This is how we implement sub-models.  The kbRoles sub-model will reside in this.kbRolesCol, be
    //of type UsersModel.userKbRoles, and we fetch it with an address relative to the main
    //user model of /kbRoles.
    kbRoles: function () {
      this.kbRolesCol = this.getSubModel(this.kbRolesCol, UsersModel.userKbRoles, "/kbRoles");
      return this.kbRolesCol;
    },

    //This is how we implement sub-models.  The roles sub-model will reside in this.rolesCol, be
    //of type UsersModel.userRoles, and we fetch it with an address relative to the main
    //user model of /roles.
    roles: function () {
      this.rolesCol = this.getSubModel(this.rolesCol, UsersModel.userRoles, "/roles");
      return this.rolesCol;
    },

    touchpoints: function () {
      this.touchpointsColl = this.getSubModel(
        this.touchpointsColl,
        UsersModel.userTouchpoints,
        "/touchpoints",
      );
      return this.touchpointsColl;
    },

    contexts: function () {
      this.contextsColl = this.getSubModel(this.contextsColl, UsersModel.userContexts, "/contexts");
      return this.contextsColl;
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.set(this.cloneCopy.attributes);
      _.each(
        this.subModels,
        function (subModel) {
          subModel.restore();
        },
        this,
      );
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      this.cloneCopy = this.clone();

      _.each(
        this.subModels,
        function (subModel) {
          subModel.backup();
        },
        this,
      );
    },
    validateAttribute: function (attribute, value, allAttributes) {
      if (attribute === "userLogin") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.userAdministration.noLogin",
            maxLength: 50,
          }) || this.validateSafeCharacters(value)
        );
      } else if (attribute === "userFullName") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.userAdministration.noName",
            maxLength: 255,
          }) || this.validateSafeCharacters(value)
        );
      } else if (attribute === "password") {
        if (value === EMPTY_PASSWORD_PLACEHOLDER) {
          return;
        }
        const isNew = allAttributes ? !allAttributes.userId : this.isNew();
        if (isNew || value.length > 0) {
          return UsersModel.validatePassword(
            value,
            allAttributes ? allAttributes.userLogin : this.get("userLogin"),
            allAttributes ? allAttributes.userFullName : this.get("userFullName"),
          );
        }
      } else if (attribute === "reenterPassword") {
        const password = allAttributes ? allAttributes.password : this.get("password");
        if (password) {
          if (!value || !value.length) {
            return $.t("user.missingPassword");
          } else if (value !== password) {
            return $.t("user.passwordMismatch");
          }
        }
      } else if (attribute === "userEmail") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.userAdministration.validEmail",
            maxLength: 255,
          }) ||
          this.validateEmailAddress(value, {
            invalidEmailMessage: "admin.userAdministration.validEmail",
          })
        );
      } else if (attribute === "userOrganization") {
        return this.validateStringLength(value, { maxLength: 255 });
      } else if (attribute === "userPhone") {
        return this.validateStringLength(value, { maxLength: 15 });
      } else if (attribute === "roles") {
        if (this.rolesCol) {
          return this.rolesCol.validate();
        }
      }
    },
  });

  UsersModel.landlordUser = Backbone.Model.extend({
    idAttribute: "userId",
    defaults: {
      userLogin: "",
      userFullName: "",
      userCreationDate: null,
      enabledStatus: USER_STATUS.ENABLED,
      isAdmin: true,
      userEmail: "",
      userId: null,
      userOrganization: "",
      userPhone: "",
      showPassword: false,
      password: "",
      reenterPassword: "",
      botCompany: "",
      botEmail: "",
      lastLoginDate: null,
    },
    defaultValidateAttributes: [
      "userLogin",
      "userFullName",
      "password",
      "userEmail",
      "userOrganization",
      "userPhone",
    ],
    isTenantUser: function () {
      return false;
    },
    url: function () {
      let landlordId = this.get("landlordId");
      if (!landlordId && this.collection) {
        landlordId = this.collection.landlordId;
      }
      if (!landlordId) {
        console.error("Could not determine landlord ID");
        return null;
      }
      let url = `${Properties.adminRootURL}/landlords/${landlordId}`;
      if (this.id) {
        url += "/" + this.id;
      }
      return url;
    },
    saveModel: function (options) {
      const modelOptions = this._prepareSaveModelOptions(options);
      if (this._allowSave(options)) {
        delete this.attributes.showPassword;
        const result = this.save(null, modelOptions);
        this.attributes.showPassword = false; //reset it to false
        return result;
      }
      //fire success callback because nothing needed to be saved
      modelOptions.success(this, {}, options);
    },
    backup: function () {
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this.wasDirty = this.dirty;
        //create a new collection and add cloned models to that collection
        this.cloneCopy = this.clone();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.set(this.cloneCopy.attributes);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    validateAttribute: function (attribute, value, allAttributes) {
      if (attribute === "userLogin") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.userAdministration.noLogin",
            maxLength: 50,
          }) || this.validateSafeCharacters(value)
        );
      } else if (attribute === "userFullName") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.userAdministration.noName",
            maxLength: 255,
          }) || this.validateSafeCharacters(value)
        );
      } else if (attribute === "password") {
        if (value === EMPTY_PASSWORD_PLACEHOLDER) {
          return;
        }
        const isNew = allAttributes ? !allAttributes.userId : this.isNew();
        if (isNew || value.length > 0) {
          return UsersModel.validatePassword(
            value,
            allAttributes ? allAttributes.userLogin : this.get("userLogin"),
            allAttributes ? allAttributes.userFullName : this.get("userFullName"),
          );
        }
      } else if (attribute === "reenterPassword") {
        const password = allAttributes ? allAttributes.password : this.get("password");
        if (password) {
          if (!value || !value.length) {
            return $.t("user.missingPassword");
          } else if (value !== password) {
            return $.t("user.passwordMismatch");
          }
        }
      } else if (attribute === "userEmail") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.userAdministration.validEmail",
            maxLength: 255,
          }) ||
          this.validateEmailAddress(value, {
            invalidEmailMessage: "admin.userAdministration.validEmail",
          })
        );
      } else if (attribute === "userOrganization") {
        return (
          this.validateStringLength(value, { maxLength: 255 }) || this.validateSafeCharacters(value)
        );
      } else if (attribute === "userPhone") {
        return this.validateStringLength(value, { maxLength: 15 });
      }
    },
  });

  UsersModel.allUsers = Backbone.Collection.extend({
    model: UsersModel.tenantUser,
    url: function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      return UsersModel.getTenantUrlFragment(tenantId);
    },
    addNewUser: function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      const newUser = new UsersModel.tenantUser({
        tenantId: tenantId,
      });
      this.add(newUser);
      return newUser;
    },
  });

  UsersModel.tenantUsers = Backbone.Collection.extend({
    model: UsersModel.tenantUser,
    initializeCustom: function (tenantId) {
      this.tenantId = tenantId;
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "userFullName");
    },
    url: function () {
      return UsersModel.getTenantUrlFragment(this.tenantId);
    },
    addNewUser: function () {
      const newUser = new UsersModel.tenantUser({
        tenantId: this.tenantId,
      });
      this.add(newUser);
      return newUser;
    },
  });

  Messenger.reqres.setHandler("tenant:users", function (tenantId) {
    const tenantUsers = new UsersModel.tenantUsers(tenantId);
    tenantUsers.fetch();
    return tenantUsers;
  });

  UsersModel.tenantAndGlobalUsers = Backbone.Collection.extend({
    model: UsersModel.tenantUser,
    initializeCustom: function (tenantId) {
      this.tenantId = tenantId;
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "userFullName");
    },
    url: function () {
      return UsersModel.getTenantAndGlobalUsersUrlFragment(this.tenantId);
    },
  });

  Messenger.reqres.setHandler("tenant:and:global:users", function (tenantId) {
    const tenantAndGlobalUsers = new UsersModel.tenantAndGlobalUsers(tenantId);
    tenantAndGlobalUsers.fetch();
    return tenantAndGlobalUsers;
  });

  UsersModel.landlordUsers = Backbone.Collection.extend({
    model: UsersModel.landlordUser,
    initializeCustom: function (landlordId) {
      this.landlordId = landlordId;
    },
    url: function () {
      return `${Properties.adminRootURL}/landlords/${this.landlordId}/users`;
    },
    addNewUser: function () {
      const newUser = new UsersModel.landlordUser({
        landlordId: this.landlordId,
      });
      this.add(newUser);
      return newUser;
    },
  });

  Messenger.reqres.setHandler("landlord:users", function (landlordId) {
    const landlordUsers = new UsersModel.landlordUsers(landlordId);
    landlordUsers.fetch();
    return landlordUsers;
  });

  UsersModel.activeUsers = Backbone.Collection.extend({
    model: UsersModel.tenantUser,
    url: function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      return UsersModel.getTenantUrlFragment(tenantId);
    },
  });

  UsersModel.inactiveUsers = Backbone.Collection.extend({
    model: UsersModel.tenantUser,
    url: function () {
      const tenantId = Messenger.reqres.request("active:tenantId");
      return UsersModel.getTenantUrlFragment(tenantId);
    },
  });

  Messenger.reqres.setHandler("user:list:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.userAdministration.fullName"),
        modelAttribute: "userFullName",
        selectable: true,
        sortable: true,
        longTextField: true,
        filterable: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.login"),
        modelAttribute: "userLogin",
        selectable: false,
        sortable: true,
        longTextField: true,
        filterable: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.email"),
        modelAttribute: "userEmail",
        selectable: false,
        sortable: true,
        longTextField: true,
        filterable: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.userStatus"),
        modelAttribute: "enabledStatus",
        selectable: false,
        sortable: true,
        hideable: true,
        customHtml: (userModel) =>
          $.t("admin.userAdministration.userStatus" + userModel.get("enabledStatus")),
      }),
      new Header({
        headerName: $.t("admin.userAdministration.phone"),
        modelAttribute: "userPhone",
        selectable: false,
        sortable: true,
        filterable: true,
        longTextField: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.organization"),
        modelAttribute: "userOrganization",
        selectable: false,
        sortable: true,
        filterable: true,
        longTextField: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.systemAdmin"),
        modelAttribute: "isAdmin",
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
        customHtml:
          '<% if (isAdmin) { %> \n\
                <i class="icon-ok icon-2x"></i> \n\
              <% } else { %> \n\
                 \n\
              <% } %>',
      }),
      new Header({
        headerName: $.t("admin.userAdministration.roles"),
        modelAttribute: "userRoles",
        selectable: false,
        sortable: false,
        filterable: false,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.createDate"),
        modelAttribute: "userCreationDate",
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
        dateField: true,
        dateOnly: true,
      }),
      new Header({
        headerName: $.t("admin.userAdministration.lastLoginDate"),
        modelAttribute: "lastLoginDate",
        selectable: false,
        sortable: true,
        hidden: false,
        hideable: true,
        dateField: true,
        dateOnly: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "userFullName",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteUser" data-i18n="[title]admin.userAdministration.deleteUser" \
              data-toggle="tooltip"><i class="icon-remove"></i></button>',
      }),
    ]);
  });

  Messenger.reqres.setHandler("user:data", function (userId) {
    const user = new UsersModel.tenantUser();
    user.set("userId", userId);
    user.fetch();
    return user;
  });

  Messenger.reqres.setHandler("user:statuses", function (includeAll) {
    const states = _.map(USER_STATUS, function (value) {
      return {
        id: value,
        text: $.t("admin.userAdministration.userStatus" + value),
      };
    });
    if (includeAll) {
      states.unshift({
        id: USER_STATUS_ALL,
        text: $.t("admin.userAdministration.userStatusAll"),
      });
    }
    return new Backbone.Collection(states);
  });

  UsersModel.getTenantUrlFragment = function (tenantId) {
    return `${Properties.adminRootURL}/tenant/${tenantId}/users`;
  };

  UsersModel.getTenantAndGlobalUsersUrlFragment = function (tenantId) {
    return `${Properties.adminRootURL}/tenant/${tenantId}/users/global`;
  };

  return UsersModel;
});
